import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../services/modal.service';
import { PortWithState, PortType } from '../../../models/port.model';
import { IosSwitchComponent } from '../../admin/tabs/user-form-modal/ios-switch/ios-switch';

export interface PortFormResult {
  type: PortType;
  portNumber: number;
  name: string;
  active: boolean;
}

const PORT_NUMBER_MIN = 0;

@Component({
  selector: 'app-port-form-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IosSwitchComponent],
  templateUrl: './port-form-modal.html',
  styleUrl: './port-form-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortFormModalComponent implements OnInit {
  @Input() port?: PortWithState;
  @Input() defaultType: PortType = 'RELAY';

  private fb = inject(FormBuilder);
  private modalService = inject(ModalService);

  form: FormGroup = this.fb.group({
    type: ['RELAY' as PortType, [Validators.required]],
    portNumber: [0, [Validators.required, Validators.min(PORT_NUMBER_MIN)]],
    name: ['', [Validators.required, nonEmptyTrimmed]],
    active: [false],
  });

  readonly portTypes: { value: PortType; label: string }[] = [
    { value: 'RELAY', label: 'Реле' },
    { value: 'SENSOR', label: 'Датчик' },
  ];

  get isEdit(): boolean {
    return !!this.port;
  }

  get isTypeLocked(): boolean {
    return this.isEdit;
  }

  get activeValue(): boolean {
    return this.form.get('active')?.value === true;
  }

  ngOnInit(): void {
    if (this.isEdit && this.port) {
      this.form.patchValue(
        {
          type: this.port.type,
          portNumber: this.port.portNumber,
          name: this.port.name,
          active: this.port.active,
        },
        { emitEvent: false },
      );

      this.form.get('type')?.disable({ emitEvent: false });
    } else {
      this.form.get('type')?.setValue(this.defaultType, { emitEvent: false });
    }
  }

  isInvalid(controlName: 'portNumber' | 'name'): boolean {
    const c = this.form.get(controlName);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  onActiveChange(value: boolean): void {
    this.form.get('active')?.setValue(value, { emitEvent: false });
    this.form.get('active')?.markAsDirty();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();

    const result: PortFormResult = {
      type: v.type as PortType,
      portNumber: Number(v.portNumber),
      name: (v.name as string).trim(),
      active: v.active === true,
    };

    this.modalService.close(result);
  }

  onCancel(): void {
    this.modalService.close();
  }
}

function nonEmptyTrimmed(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '') as string;
  return value.trim().length > 0 ? null : { required: true };
}
